import { getUserPreferences } from "@workspace/shared-utils";
import { CACHE_TIERS } from "@workspace/types";
import type { DateTimePreferences } from "@workspace/types/src/entities/user";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { trpc } from "@/lib/trpc/react";

/**
 * Hook to fetch and manage user preferences
 *
 * Fetches user's time, date format, and location preferences using tRPC
 * with intelligent fallback to system defaults when user hasn't set preferences.
 *
 * Preference types:
 * - time_format: "12h" | "24h"
 * - date_format: "us" | "international"
 * - location_preference: string | null (city or metro area name)
 *
 * Caching strategy:
 * - 5-minute stale time (preferences change infrequently)
 * - No retry on failure (graceful degradation to defaults)
 * - Disabled when user not authenticated
 *
 * @returns DateTimePreferences object with time_format, date_format, and location_preference
 *
 * @example
 * ```tsx
 * const preferences = useUserPreferences()
 *
 * const formatTime = (date: Date) => {
 *   return preferences.time_format === "12h"
 *     ? date.toLocaleTimeString("en-US", { hour12: true })
 *     : date.toLocaleTimeString("en-US", { hour12: false })
 * }
 *
 * const defaultCity = preferences.location_preference || "all"
 * ```
 *
 * Note: Returns user preferences with fallback to defaults via getUserPreferences utility
 */
export function useUserPreferences(): DateTimePreferences {
	const { user } = useSupabase();

	const { data: userProfile } = trpc.settings.getPreferences.useQuery(
		undefined,
		{
			...CACHE_TIERS.REALTIME, // Real-time for user preferences
			retry: false, // Don't retry on failure, just use defaults
			enabled: !!user, // Only run query if user is authenticated
		},
	);

	// Use the utility function to safely extract preferences with defaults
	// The tRPC endpoint returns the same format as before
	return getUserPreferences(userProfile);
}
